import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { StatusIcon } from '@/components/ui/StatusIcon';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { AlertSummary } from '@/types/api';

interface AlertsSummaryProps {
  alerts: AlertSummary[];
}

export function AlertsSummary({ alerts }: AlertsSummaryProps) {
  if (alerts.length === 0) {
    return null;
  }

  // Show max 3 alerts
  const displayAlerts = alerts.slice(0, 3);
  const hasMore = alerts.length > 3;

  return (
    <Card
      className={cn(
        'border-l-4 border-l-status-warning',
        'shadow-status-warning/10'
      )}
    >
      <Card.Header action={
        <Link
          to="/alerts"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors"
        >
          View all
          <ArrowRight className="h-4 w-4" />
        </Link>
      }>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-status-warning" />
          <span>Active Alerts</span>
          <span className="ml-1 text-sm font-normal text-gray-500">
            ({alerts.length})
          </span>
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        <div className="divide-y divide-gray-100">
          {displayAlerts.map((alert) => (
            <AlertRow key={alert.id} alert={alert} />
          ))}
        </div>

        {hasMore && (
          <div className="px-5 py-3 bg-gray-50/50 text-center">
            <Link
              to="/alerts"
              className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
            >
              +{alerts.length - 3} more alerts
            </Link>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function AlertRow({ alert }: { alert: AlertSummary }) {
  const timeAgo = formatDistanceToNow(new Date(alert.triggered_at), {
    addSuffix: true,
  });

  return (
    <div className="flex items-start gap-3 px-5 py-4 hover:bg-gray-50/50 transition-colors">
      <StatusIcon
        status={alert.severity === 'critical' ? 'critical' : 'warning'}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">{alert.pond_name}</p>
          <span className="text-xs text-gray-400">|</span>
          <span
            className={cn(
              'text-xs font-medium px-1.5 py-0.5 rounded',
              alert.severity === 'critical'
                ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-700'
            )}
          >
            {formatAlertType(alert.alert_type)}
          </span>
        </div>
        <p className="text-sm text-gray-600 mt-0.5 truncate">{alert.message}</p>
      </div>
      <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
        {timeAgo}
      </span>
    </div>
  );
}

function formatAlertType(type: string): string {
  // Convert snake_case to Title Case
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
